/**
 * Minimal, allocation-light SSE frame decoder for the agent turn stream (FEAT-1698).
 *
 * `EventSource` cannot be used because the turn is a POST with an `Authorization` header, so the
 * panel reads `response.body` directly and decodes frames here. The decoder tolerates everything
 * the backend can legally emit: `\r\n` line endings, comment heartbeats (`: heartbeat …`), frames
 * split across chunk boundaries, multi-line `data:` fields, and unknown fields such as `id:`.
 */
import { AgentStreamEvent, parseAgentStreamEvent } from './protocol';

const FRAME_FIELD_EVENT = 'event';
const FRAME_FIELD_DATA = 'data';

type SseFrameDecoder = {
  /** Decodes a raw chunk into zero or more parsed agent events. */
  push: (chunk: string) => AgentStreamEvent[];
  /** Flushes a trailing frame that the server closed without a blank-line terminator. */
  flush: () => AgentStreamEvent[];
};

/** Creates a stateful decoder that turns raw SSE text chunks into parsed agent events. */
export function createSseFrameDecoder(): SseFrameDecoder {
  let buffer = '';

  const decodeFrame = (frame: string): AgentStreamEvent | null => {
    let eventName = '';
    const dataLines: string[] = [];

    for (const rawLine of frame.split('\n')) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      // Comment lines (heartbeats) and blank lines carry no event data.
      if (line.length === 0 || line.startsWith(':')) {
        continue;
      }
      const separatorIndex = line.indexOf(':');
      const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
      const rawValue = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1);
      const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

      if (field === FRAME_FIELD_EVENT) {
        eventName = value;
      } else if (field === FRAME_FIELD_DATA) {
        dataLines.push(value);
      }
    }

    if (eventName.length === 0 || dataLines.length === 0) {
      return null;
    }
    return parseAgentStreamEvent(eventName, dataLines.join('\n'));
  };

  const drain = (): AgentStreamEvent[] => {
    const events: AgentStreamEvent[] = [];
    for (;;) {
      const boundary = findFrameBoundary(buffer);
      if (!boundary) {
        return events;
      }
      const frame = buffer.slice(0, boundary.index);
      buffer = buffer.slice(boundary.index + boundary.length);
      const event = decodeFrame(frame);
      if (event) {
        events.push(event);
      }
    }
  };

  return {
    push: (chunk: string) => {
      buffer += chunk;
      return drain();
    },
    flush: () => {
      const remaining = buffer;
      buffer = '';
      const event = remaining.trim().length > 0 ? decodeFrame(remaining) : null;
      return event ? [event] : [];
    },
  };
}

/** Finds the earliest blank-line frame terminator, accepting both LF and CRLF endings. */
function findFrameBoundary(buffer: string): { index: number; length: number } | null {
  const lfIndex = buffer.indexOf('\n\n');
  const crlfIndex = buffer.indexOf('\r\n\r\n');
  if (lfIndex === -1 && crlfIndex === -1) {
    return null;
  }
  if (crlfIndex !== -1 && (lfIndex === -1 || crlfIndex < lfIndex)) {
    return { index: crlfIndex, length: 4 };
  }
  return { index: lfIndex, length: 2 };
}

/**
 * Reads an SSE response body to completion, invoking `onEvent` for every decoded agent event.
 *
 * Callers own cancellation through the `AbortSignal` passed to `fetch`; an aborted read surfaces as
 * a rejected `reader.read()` which the hook translates into a cancelled turn.
 */
export async function readAgentSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: AgentStreamEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const decodeFrames = createSseFrameDecoder();

  try {
    for (let done = false; !done;) {
      // eslint-disable-next-line no-await-in-loop -- stream chunks must be consumed sequentially
      const result = await reader.read();
      done = result.done;
      if (result.value) {
        decodeFrames.push(decoder.decode(result.value, { stream: true })).forEach(onEvent);
      }
    }
    decodeFrames.flush().forEach(onEvent);
  } finally {
    reader.releaseLock();
  }
}
